/**
 * tabコマンドハンドラ
 *
 * JSON形式のTabState/TabConfigを受け取り、タブを開く、または既存タブの状態を更新
 *
 * 使用例（新規タブ作成）:
 * x:tab {"type":"sql-editor","title":"My Query","tabViewState":{"query":"SELECT * FROM data"}}
 *
 * 使用例（既存タブ更新）:
 * x:tab {"tabId":"sql-editor-123456","tabViewState":{"query":"SELECT count(*) FROM data"}}
 */

import type { CommandHandler, CommandResult, ParsedCommand, CommandContext } from '../types';
import type { TabState } from '../../../types';
import { createTabId } from '../../../types';
import { TabRegistry, isValidTabType } from '../../tab-system/TabRegistry';
import logger from '../../utils/logger';

/**
 * x:tabコマンドで受け付けるJSON形式
 */
interface TabInput {
  // 新規タブ作成時に必須
  type?: string;
  title?: string;
  metadata?: Record<string, unknown>;
  // 既存タブ更新時に指定
  tabId?: string;
  // 両方で使用
  tabViewState?: Record<string, unknown>;
}

export const tabHandler: CommandHandler = {
  name: 'tab',
  description: 'Open a new tab or update existing tab with JSON configuration',
  category: 'navigation',
  parameters: [
    {
      name: 'json',
      type: 'string',
      required: false, // JSON-basedコマンドはpositionalArgsを使用するため、ここではfalse
      description: 'JSON object specifying tab type (new) or tabId (update) and configuration'
    }
  ],
  aliases: [],
  examples: [
    'x:tab {"type":"sql-editor","title":"My Query","tabViewState":{"query":"SELECT * FROM data"}}',
    'x:tab {"type":"crosstab","tabViewState":{"rowFields":["category"],"columnFields":["month"]}}',
    'x:tab {"tabId":"sql-editor-123456","tabViewState":{"query":"SELECT count(*) FROM data"}}'
  ],

  execute: async (args: ParsedCommand, context: CommandContext): Promise<CommandResult> => {
    // JSONを取得（位置引数全体を結合）
    const jsonStr = args.positionalArgs.join(' ').trim();

    if (!jsonStr) {
      return {
        success: false,
        message: 'JSON configuration required',
        error: {
          code: 'NO_JSON',
          message: 'No JSON configuration provided',
          suggestion: 'Provide a JSON object: x:tab {"type":"sql-editor"}'
        }
      };
    }

    // JSONをパース
    let tabInput: TabInput;
    try {
      tabInput = JSON.parse(jsonStr) as TabInput;
    } catch (e) {
      logger.error('[tabHandler] Failed to parse JSON', { jsonStr, error: e });
      return {
        success: false,
        message: 'Invalid JSON format',
        error: {
          code: 'INVALID_JSON',
          message: `Failed to parse JSON: ${e instanceof Error ? e.message : String(e)}`,
          suggestion: 'Check JSON syntax'
        }
      };
    }

    // 既存タブの更新か新規タブの作成かを判定
    if (tabInput.tabId) {
      // 既存タブの更新
      if (!tabInput.tabViewState) {
        return {
          success: false,
          message: 'tabViewState required for update',
          error: {
            code: 'NO_TAB_VIEW_STATE',
            message: 'tabViewState is required when updating existing tab',
            suggestion: 'Include "tabViewState" field in JSON'
          }
        };
      }

      logger.info('[tabHandler] Updating existing tab', {
        tabId: tabInput.tabId,
        tabViewStateKeys: Object.keys(tabInput.tabViewState)
      });

      const success = context.updateTabViewState(tabInput.tabId, tabInput.tabViewState);
      if (!success) {
        return {
          success: false,
          message: `Tab not found: ${tabInput.tabId}`,
          error: {
            code: 'TAB_NOT_FOUND',
            message: `Could not find tab with id: ${tabInput.tabId}`,
            suggestion: 'Check the tabId is correct'
          }
        };
      }

      return {
        success: true,
        message: `Updated tab ${tabInput.tabId}`,
        tabId: tabInput.tabId,
        data: {
          tabId: tabInput.tabId,
          updated: true
        }
      };
    }

    // 新規タブの作成
    // タブタイプの検証
    if (!tabInput.type) {
      return {
        success: false,
        message: 'Tab type required',
        error: {
          code: 'NO_TYPE',
          message: 'Tab type is required for new tab (or provide tabId to update existing)',
          suggestion: 'Include "type" field in JSON'
        }
      };
    }

    if (!isValidTabType(tabInput.type)) {
      const validTypes = Object.keys(TabRegistry).join(', ');
      return {
        success: false,
        message: `Invalid tab type: ${tabInput.type}`,
        error: {
          code: 'INVALID_TYPE',
          message: `Unknown tab type: ${tabInput.type}`,
          suggestion: `Valid types: ${validTypes}`
        }
      };
    }

    // タブタイプに応じてTabStateを構築
    const tabType = tabInput.type as TabState['type'];
    const registryEntry = TabRegistry[tabType];

    // タブIDを生成
    const timestamp = Date.now();
    const random = Math.random().toString(36).substr(2, 9);
    const tabId = createTabId(`${tabType}-${timestamp}_${random}`);

    // デフォルトのタイトルを取得
    const defaultTitle = registryEntry.label || tabType;

    // TabStateを構築
    const tab: TabState = {
      id: tabId,
      type: tabType,
      title: tabInput.title || defaultTitle,
      isClosable: true,
      keepMounted: registryEntry.persistDOM,
      metadata: tabInput.metadata as TabState['metadata'],
      tabViewState: tabInput.tabViewState
    };

    logger.info('[tabHandler] Creating tab from JSON', {
      type: tabType,
      title: tab.title,
      hasTabViewState: !!tabInput.tabViewState
    });

    // タブを追加
    context.addTab(tab);

    return {
      success: true,
      message: `Opened ${tab.title} tab`,
      tabId: tab.id,
      data: {
        type: tabType,
        title: tab.title
      }
    };
  }
};
